import type { SkyscannerMichelinSearchV1Accommodationssection } from '@skyscanner-internal/michelin-web-http-client';

type Accommodation = SkyscannerMichelinSearchV1Accommodationssection.Accommodation;

function getAccommodationItemPriceAmount(accommodation: Accommodation): number | null {
  if (accommodation.price?.price?.amount) {
    return parseInt(accommodation.price.price.amount, 10);
  }
  return null;
}

// Compares such that highest rated will be first
const compareAccomodationItemsByRating = (a: Accommodation, b: Accommodation): number => {
  if (a.stars && b.stars) {
    if (a.stars > b.stars) return -1;
    if (a.stars < b.stars) return 1;
  } else if (a.stars && !b.stars) {
    return -1;
  } else if (!a.stars && b.stars) {
    return 1;
  }
  return 0; // stars ratings are equal or both undefined
};

// Compares such that cheapest price will be first
// Note. This currently assumes that the accommodation unit and currency are the same for both items
// Items with missing prices are sorted last
// For items with equal prices, sorts by ID for deterministic ordering
const compareAccommodationItemsByPrice = (a: Accommodation, b: Accommodation): number => {
  const priceA = getAccommodationItemPriceAmount(a);
  const priceB = getAccommodationItemPriceAmount(b);

  // Both have prices - compare them
  if (priceA !== null && priceB !== null) {
    if (priceA !== priceB) {
      return priceA - priceB; // Ascending order (cheapest first)
    }
    // Prices are equal, use ID as tie-breaker for deterministic ordering
    return (a.id || '').localeCompare(b.id || '');
  }

  // Items with prices come before items without prices
  if (priceA !== null && priceB === null) {
    return -1;
  }
  if (priceA === null && priceB !== null) {
    return 1;
  }

  // Both have no price, use ID as tie-breaker
  return (a.id || '').localeCompare(b.id || '');
};

const compareAccommodationItemsByRatingThenPrice = (a: Accommodation, b: Accommodation): number => {
  let ratingComparison = compareAccomodationItemsByRating(a, b);
  if (ratingComparison === 0) {
    ratingComparison = compareAccommodationItemsByPrice(a, b);
  }
  return ratingComparison;
};

export {
  compareAccomodationItemsByRating,
  compareAccommodationItemsByPrice,
  compareAccommodationItemsByRatingThenPrice,
};
